import React, { useCallback, useEffect, useState } from 'react';

import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../ui/card';
import { Heading } from '../../ui/heading';
import { Input } from '../../ui/input';
import { Timestamp } from '../../ui/timestamp';
import { fetchWithAuth } from '../../../utils/api';

interface ProviderKeyStatus {
  provider: 'openai';
  label: string;
  configured: boolean;
  masked_value: string | null;
  validation_status: 'valid' | 'invalid' | 'unknown';
  last_validated_at: string | null;
  last_validation_error: string | null;
  updated_at: string | null;
}

const PROVIDER_KEY_STATUS_EVENT = 'billing-provider-key-status-updated';

const STATUS_BADGE_STYLES: Record<ProviderKeyStatus['validation_status'], string> = {
  valid: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  invalid: 'border-rose-200 bg-rose-50 text-rose-700',
  unknown: 'border-slate-200 bg-slate-50 text-slate-600',
};

const STATUS_LABELS: Record<ProviderKeyStatus['validation_status'], string> = {
  valid: 'Validated',
  invalid: 'Needs attention',
  unknown: 'Not validated',
};

const BillingProviderKeysView: React.FC = () => {
  const [status, setStatus] = useState<ProviderKeyStatus | null>(null);
  const [draftValue, setDraftValue] = useState('');
  const [revealedValue, setRevealedValue] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [revealing, setRevealing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const publishProviderKeyStatus = (nextStatus: ProviderKeyStatus | null) => {
    window.dispatchEvent(
      new CustomEvent<ProviderKeyStatus | null>(PROVIDER_KEY_STATUS_EVENT, {
        detail: nextStatus,
      }),
    );
  };

  const loadProviderKeyStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = (await fetchWithAuth('/v1/billing/provider-keys')) as ProviderKeyStatus[];
      const openaiStatus =
        response.find((item) => item.provider === 'openai') ?? {
          provider: 'openai',
          label: 'OpenAI',
          configured: false,
          masked_value: null,
          validation_status: 'unknown',
          last_validated_at: null,
          last_validation_error: null,
          updated_at: null,
        };
      setStatus(openaiStatus);
      publishProviderKeyStatus(openaiStatus);
    } catch (fetchError) {
      console.error('Failed to load provider key status', fetchError);
      setError(fetchError instanceof Error ? fetchError.message : 'Failed to load provider key status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadProviderKeyStatus();
  }, [loadProviderKeyStatus]);

  const saveProviderKey = async () => {
    const value = draftValue.trim();
    if (!value) {
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const response = (await fetchWithAuth('/v1/billing/provider-keys/openai', {
        method: 'PUT',
        body: JSON.stringify({ value }),
      })) as ProviderKeyStatus;
      setStatus(response);
      publishProviderKeyStatus(response);
      setDraftValue('');
      setRevealedValue(null);
    } catch (fetchError) {
      console.error('Failed to save provider key', fetchError);
      setError(fetchError instanceof Error ? fetchError.message : 'Failed to save provider key');
    } finally {
      setSaving(false);
    }
  };

  const deleteProviderKey = async () => {
    const confirmed = window.confirm(
      'Delete the organization OpenAI provider key? A valid organization key is required for any org-scoped LLM usage.',
    );
    if (!confirmed) {
      return;
    }

    setDeleting(true);
    setError(null);
    try {
      await fetchWithAuth('/v1/billing/provider-keys/openai', {
        method: 'DELETE',
      });
      setRevealedValue(null);
      publishProviderKeyStatus({
        provider: 'openai',
        label: 'OpenAI',
        configured: false,
        masked_value: null,
        validation_status: 'unknown',
        last_validated_at: null,
        last_validation_error: null,
        updated_at: null,
      });
      await loadProviderKeyStatus();
    } catch (fetchError) {
      console.error('Failed to delete provider key', fetchError);
      setError(fetchError instanceof Error ? fetchError.message : 'Failed to delete provider key');
    } finally {
      setDeleting(false);
    }
  };

  const revealProviderKey = async () => {
    setRevealing(true);
    setError(null);
    try {
      const response = (await fetchWithAuth(
        '/v1/billing/provider-keys/openai/reveal',
      )) as { provider: 'openai'; value: string };
      setRevealedValue(response.value);
    } catch (fetchError) {
      console.error('Failed to reveal provider key', fetchError);
      setError(fetchError instanceof Error ? fetchError.message : 'Failed to reveal provider key');
    } finally {
      setRevealing(false);
    }
  };

  if (loading) {
    return (
      <div className="h-full px-4 py-5 md:px-6 md:py-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 w-56 rounded bg-slate-200" />
          <div className="h-72 rounded-3xl bg-slate-100" />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full px-4 py-5 md:px-6 md:py-6">
      <div className="mb-6">
        <Heading level={2} className="font-jakarta text-2xl font-extrabold tracking-[-0.02em] text-foreground">
          LLM provider keys
        </Heading>
        <p className="mt-2 max-w-3xl text-[13px] text-muted-foreground">
          Configure organization-scoped provider credentials for LLM usage. When an organization
          key is set, Good Outcomes will use that key and will not silently fall back to the
          platform key if it stops working.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6">
        {error && (
          <Card className="border-rose-200 bg-rose-50 shadow-none">
            <CardContent className="p-4 text-sm text-rose-700">{error}</CardContent>
          </Card>
        )}

        <Card className="border-slate-200/80 shadow-sm">
          <CardHeader className="gap-3 border-b border-slate-100">
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div>
                <CardTitle>{status?.label ?? 'OpenAI'}</CardTitle>
                <CardDescription className="mt-1 max-w-2xl">
                  This key is used for organization-scoped LLM calls and future billing analytics
                  attribution.
                </CardDescription>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline" className={STATUS_BADGE_STYLES[status?.validation_status ?? 'unknown']}>
                  {STATUS_LABELS[status?.validation_status ?? 'unknown']}
                </Badge>
                <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-600">
                  {status?.configured ? 'Configured' : 'Not configured'}
                </Badge>
              </div>
            </div>
          </CardHeader>

          <CardContent className="space-y-6 p-6">
            <div className="grid gap-4 lg:grid-cols-3">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                  Current value
                </p>
                <p className="mt-2 text-sm font-medium text-slate-900">
                  {status?.masked_value ?? 'No organization key stored'}
                </p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                  Last validated
                </p>
                <p className="mt-2 text-sm font-medium text-slate-900">
                  <Timestamp value={status?.last_validated_at} format="absolute" fallback="Not yet validated" />
                </p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                  Failure handling
                </p>
                <p className="mt-2 text-sm font-medium text-slate-900">
                  Broken org keys block org-scoped LLM calls until fixed.
                </p>
              </div>
            </div>

            {status?.last_validation_error && (
              <Card className="border-rose-200 bg-rose-50 shadow-none">
                <CardContent className="p-4 text-sm text-rose-700">
                  {status.last_validation_error}
                </CardContent>
              </Card>
            )}

            {revealedValue && (
              <Card className="border-emerald-200 bg-emerald-50 shadow-none">
                <CardContent className="space-y-2 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.12em] text-emerald-700">
                    Revealed value
                  </p>
                  <code className="block break-all whitespace-pre-wrap text-sm text-emerald-950">
                    {revealedValue}
                  </code>
                </CardContent>
              </Card>
            )}

            <div className="space-y-3">
              <div>
                <Heading level={4}>Update provider key</Heading>
                <p className="mt-1 text-sm text-slate-500">
                  The key is validated before it is stored. Invalid keys are rejected and not
                  saved.
                </p>
              </div>
              <div className="flex flex-col gap-3 xl:flex-row">
                <Input
                  type="password"
                  value={draftValue}
                  onChange={(event) => setDraftValue(event.target.value)}
                  placeholder={status?.configured ? 'Replace OpenAI key' : 'Enter OpenAI key'}
                  className="bg-white"
                />
                <Button
                  onClick={() => void saveProviderKey()}
                  disabled={saving || !draftValue.trim()}
                  loading={saving}
                >
                  Save provider key
                </Button>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <Button
                variant="outline"
                onClick={() => void revealProviderKey()}
                disabled={revealing || !status?.configured}
              >
                {revealing ? 'Revealing...' : 'Reveal stored key'}
              </Button>
              <Button
                variant="outline"
                onClick={() => void loadProviderKeyStatus()}
              >
                Refresh status
              </Button>
              <Button
                variant="outline"
                onClick={() => void deleteProviderKey()}
                disabled={deleting || !status?.configured}
                className="border-rose-200 text-rose-700 hover:bg-rose-50 hover:text-rose-700"
              >
                {deleting ? 'Deleting...' : 'Delete provider key'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BillingProviderKeysView;
